import React, { createContext, useContext, useEffect, useState } from 'react';

const getBackendUrl = () => process.env.REACT_APP_BACKEND_URL || '';

/**
 * Default flags used before the fetch resolves (and if it fails). Keep these as the
 * conservative "pre-launch" state so copy never overclaims broker support.
 */
const DEFAULT_FLAGS = {
  connectTradeLaunched: false,
  supportedBrokerCount: null,
};

const FeatureFlagsContext = createContext({
  flags: DEFAULT_FLAGS,
  isLoading: true,
});

export const useFeatureFlags = () => useContext(FeatureFlagsContext);

/**
 * Convenience hook for the single master relaunch flag (Connect Trade multi-broker launch).
 * Gate any "we support N brokers" / broker-comparison copy behind this instead of hardcoding.
 */
export const useConnectTradeLaunched = () => useFeatureFlags().flags.connectTradeLaunched;

/**
 * Hacky "LaunchDarkly-lite": fetches a tiny, no-auth, cacheable flag payload from the backend
 * (`GET /config/flags`) once on mount. No auth required — this must resolve on the logged-out
 * marketing home page too. Falls back to pre-launch defaults on any failure.
 */
export const FeatureFlagsProvider = ({ children }) => {
  const [flags, setFlags] = useState(DEFAULT_FLAGS);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const response = await fetch(`${getBackendUrl()}/config/flags`);
        if (!response.ok) throw new Error(`Unexpected status ${response.status}`);
        const data = await response.json();
        if (!cancelled && data?.flags) {
          setFlags({ ...DEFAULT_FLAGS, ...data.flags });
        }
      } catch (error) {
        // Silent fallback to defaults — this endpoint should never block or break page render.
        console.warn('[FeatureFlags] Failed to load /config/flags, using defaults:', error);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <FeatureFlagsContext.Provider value={{ flags, isLoading }}>
      {children}
    </FeatureFlagsContext.Provider>
  );
};

export default FeatureFlagsContext;
